/**
 * CopilotComposer - the one place you type to the copilot.
 *
 * There were two. The new-conversation screen had a field sitting inline in a
 * scrolling column, halfway down between the greeting and the suggestions; an
 * open conversation had a different field, differently styled, pinned to the
 * bottom of the panel. Same control, two shapes, two positions - and on the
 * screen whose entire purpose is "type your first question" it was the one
 * element that scrolled away.
 *
 * So: one component, and the caller mounts it in the footer either way. The
 * place you type is the bottom of the overlay, always, which is where every
 * chat in the world has trained people to look.
 *
 * The shell carries the border and the focus ring rather than the textarea,
 * because the button lives inside the field. A field and a button standing
 * side by side never line up once the field grows.
 */
import { Send } from "lucide-react";
import { useLayoutEffect, useRef } from "react";

import { Button } from "@/shared/ui/Button";
import { cn } from "@/shared/lib/cn";

export function CopilotComposer({
  value,
  onChange,
  onSubmit,
  disabled = false,
  placeholder,
  /** Word on the button. Omitted, it is a send arrow with a screen-reader name. */
  submitLabel,
  /** Shown under the field, left of the button. Keep it to one short line. */
  hint,
  /** Rendered above the field inside the shell - errors, status notes. */
  children,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder: string;
  submitLabel?: string;
  hint?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Before paint, or the field visibly jumps a row as you type.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div className={className}>
      {children}
      <div
        className={cn(
          "rounded-xl border border-border bg-background transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-ring motion-reduce:transition-none",
          disabled && "opacity-60",
        )}
      >
        <textarea
          ref={ref}
          rows={1}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter is a newline. Same contract everywhere.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (canSend) onSubmit();
            }
          }}
          placeholder={placeholder}
          // Grows with the question to a ceiling, then scrolls. A fixed height
          // is both too tall for "how many meals today?" and too short for
          // anything worth typing.
          // focus-visible:outline-none as well as outline-none: the global
          // focus-visible rule in index.css outranks the plain one, and the
          // field was drawing its own ring inside the shell's ring.
          className="block max-h-40 w-full resize-none bg-transparent px-3.5 pt-3 pb-1 text-sm text-foreground outline-none focus:outline-none focus-visible:outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
        <div className="flex items-center justify-between gap-3 px-3.5 pb-2.5">
          <span className="min-w-0 truncate text-2xs text-muted-foreground">
            {hint ?? ""}
          </span>
          {submitLabel ? (
            <Button
              variant="primary"
              disabled={!canSend}
              onClick={onSubmit}
              className="shrink-0"
            >
              {submitLabel}
            </Button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSend}
              aria-label="Send message"
              className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring motion-reduce:transition-none"
            >
              <Send size={15} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
